import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import useMyCafes from '../api/user/useMyCafes';
import { User } from '../apiTypes';
import Loader from '../components/Loader';
import { auth } from '../firebase';
import AuthPage from '../pages/AuthPage';
import { useCollection } from '../utils/api';
import useInitTracking from '../utils/useInitTracking';

type UserContextValue = {
  user: DbDocument<User>;
  cafeIds: string[];
  fetchCafes: () => Promise<void>;
  isLoading: boolean;
};

const UserContext = createContext<UserContextValue | null>(null);

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export const UserProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const [_, setUserChecked] = useState(0);
  const { pathname } = useLocation();
  const [userAuth, isUserAuthLoading] = useAuthState(auth);

  const phoneNumber = userAuth?.phoneNumber || (!userAuth?.phoneNumber && !userAuth?.email && userAuth?.displayName);

  useEffect(() => {
    if (userAuth?.uid && !userAuth?.phoneNumber && !userAuth?.email && !userAuth?.displayName) {
      setTimeout(() => {
        setUserChecked((prev) => prev + 1);
      }, 1000);
    }
  }, [userAuth?.displayName, userAuth?.email, userAuth?.phoneNumber, userAuth?.uid]);

  const { data: usersByEmail, isValidating: isValidatingByMail } = useCollection<User>(
    userAuth?.email ? `users` : null,
    {
      where: ['email', '==', userAuth?.email || null],
    }
  );

  const { data: usersByPhone, isValidating: isValidatingByPhone } = useCollection<User>(phoneNumber ? `users` : null, {
    where: ['phone', '==', phoneNumber || null],
  });

  const userDoc = usersByPhone?.[0] || usersByEmail?.[0];

  useInitTracking(userDoc);

  const { cafeIds, isLoading, fetchCafes } = useMyCafes({
    email: userDoc?.email || null,
    phone: userDoc?.phone || null,
  });

  const value = useMemo(
    () =>
      userDoc
        ? {
            user: userDoc,
            cafeIds,
            fetchCafes,
            isLoading,
          }
        : undefined,
    [cafeIds, userDoc, fetchCafes, isLoading]
  );

  useEffect(() => {
    if (!isLoading && userDoc && !cafeIds.length && !(pathname === '/' || pathname === '/create')) {
      navigate('/');
    }
  }, [cafeIds.length, isLoading, navigate, pathname, userDoc]);

  if (isUserAuthLoading || isValidatingByMail || isValidatingByPhone) {
    return <Loader fullScreen open />;
  }

  if (!userAuth || !value) {
    return <AuthPage />;
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
